import {
    Button,
    IPlatform,
    IRestaurant,
    Label,
    PlatformName,
    RestaurantSubscription,
    Switch,
    useConfirmContext,
} from '@localina/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAccount } from '../../../api/queries/account';
import { useActivePlatforms } from '../../../api/queries/platforms';
import { AccountUtils } from '../../../utils';
import { getPlatformIcon } from '../../../utils/PlatformUtils';

interface IPlatformsIntegration {
    restaurant: IRestaurant;
    setActivatedPlatforms: (platforms: string[]) => void;
    activatedPlatforms: string[];
    buttons?: IButtonProps[];
    wizard?: boolean;
}

interface IButtonProps {
    label: string;
    variant: 'primary' | 'secondary';
    onClick: () => void;
}

export const PlatformsIntegration = (props: IPlatformsIntegration) => {
    const { t } = useTranslation();
    const { alert, confirm } = useConfirmContext();

    const platformsQuery = useActivePlatforms();
    const accountQuery = useGetAccount();

    const handlePlatformEnabled = (platform: IPlatform) => async (value: boolean) => {
        if (!isPlatformAllowed(platform.name)) {
            alert({
                msg: t('common.features.platforms'),
                title: t('common.features.missingFeatureTitle'),
            });
            return;
        }
        if (platform.name === PlatformName.GOOGLE && value) {
            if (
                (await confirm({
                    title: t('integrations.items.platforms.googleTermsAlert.title'),
                    msg: t('integrations.items.platforms.labels.infoGoogle'),
                    cancelLabel: t('common.buttons.cancel'),
                    confirmLabel: t('common.buttons.ok'),
                })) === 'yes'
            ) {
                props.setActivatedPlatforms([...props.activatedPlatforms, platform.platformId]);
            }
        } else {
            props.setActivatedPlatforms(
                value
                    ? [...props.activatedPlatforms, platform.platformId]
                    : props.activatedPlatforms.filter((it) => it !== platform.platformId),
            );
        }
    };

    const isPlatformAllowed = (platformName: PlatformName): boolean => {
        return (
            AccountUtils.getSubscription(accountQuery.data, props.restaurant.id) === RestaurantSubscription.STANDARD ||
            platformName !== PlatformName.GOOGLE
        );
    };
    const getInfoText = (platformName: string): string | undefined => {
        switch (platformName) {
            case PlatformName.GOOGLE:
                return t('integrations.items.platforms.labels.infoGoogle');
            case PlatformName.LOCALCH:
                return t('integrations.items.platforms.labels.infoLocalCh');
        }
    };

    // The info texts are "<intro>\n\n1. …\n\n2. …": shown as a sentence and a numbered list.
    const renderRequirements = (platformName: string) => {
        const info = getInfoText(platformName);
        if (!info) {
            return null;
        }
        const [intro, ...requirements] = info
            .split(/\n\s*\n/)
            .map((part) => part.trim())
            .filter(Boolean);
        return (
            <div className="platform-card__requirements">
                <p>{intro}</p>
                {requirements.length > 0 && (
                    <ol>
                        {requirements.map((requirement) => (
                            <li key={requirement}>{requirement.replace(/^\d+\.\s*/, '')}</li>
                        ))}
                    </ol>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={'platforms-content-container'}>
                {platformsQuery.data?.map((platform) => (
                    <div className="platform-card" key={platform.platformId}>
                        <Switch
                            icon={<span className="platform-card__icon">{getPlatformIcon(platform.name)}</span>}
                            label={t([`integrations.items.platforms.names.${platform.name}`, platform.name])}
                            description={t([`integrations.items.platforms.labels.${platform.name}`, ''])}
                            checked={props.activatedPlatforms.includes(platform.platformId)}
                            onChange={handlePlatformEnabled(platform)}
                        />
                        {renderRequirements(platform.name)}
                    </div>
                ))}
                {props.wizard === true && <Label type={'text'} value={t('integrations.items.platforms.info')} />}
            </div>
            {props.buttons !== undefined && (
                <div className="buttons">
                    {props.buttons.map(({ label, variant, onClick }) => (
                        <Button label={label} onClick={onClick} secondary={variant === 'secondary'} key={label} />
                    ))}
                </div>
            )}
        </>
    );
};
